import React, { useState, useEffect } from 'react';
import { round3 } from '../../utils/money';
import { useNavigate } from 'react-router-dom';
import {
  IcSearch,
  IcClose,
  IcPlus,
  IcTruck,
  IcBuilding,
  IcReceiptText,
  IcBanknote,
  IcChevronRight,
  IcAlertCircle,
} from '../../theme/icons';
import FournisseursLabosSwitch from '../../components/FournisseursLabosSwitch';
import supplierService from '../../services/supplierService';
import factureService from '../../services/factureService';
import paymentService from '../../services/paymentService';
import authService from '../../services/authService';
import PhoneLink from '../../components/PhoneLink';
import { filterSearch } from '../../utils/search';

const formatDue = (value) => `${(Number(value) || 0).toFixed(3)} DT`;

export default function MobileSupplierList() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);

  // Supplier autocomplete + acompte (mirrors the desktop supplier list)
  const [pickedSupplier, setPickedSupplier] = useState(null);
  const [showSupplierSuggest, setShowSupplierSuggest] = useState(false);
  const [supplierFactures, setSupplierFactures] = useState([]);
  const [acompte, setAcompte] = useState('');
  const [applyingAcompte, setApplyingAcompte] = useState(false);
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [acompteError, setAcompteError] = useState(null);

  const canManage = authService.isManagerOrAdmin();

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const data = await supplierService.getAllSuppliers();
      // Handle both paginated and non-paginated responses
      const suppliersData = Array.isArray(data) ? data : data.results || [];
      setSuppliers(suppliersData);
      setFilteredSuppliers(suppliersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Impossible de charger les fournisseurs. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Client-side filter (same fields as the desktop list)
  useEffect(() => {
    // Règle Ki Para (utils/search.js) : début de mot sur le nom, « contient »
    // sur téléphone / e-mail, « commence par » en tête.
    setFilteredSuppliers(filterSearch(suppliers, searchTerm, {
      text: (s) => s.name, codes: (s) => [s.phone, s.email],
    }));
  }, [searchTerm, suppliers]);

  // Supplier suggestions for the search box (only when typing & nothing picked)
  const supplierSuggestions =
    !pickedSupplier && searchTerm.trim()
      ? filterSearch(suppliers, searchTerm, { text: (s) => s.name, codes: (s) => [s.phone] })
          .slice(0, 8)
      : [];

  // Outstanding total of the picked supplier (sum of its factures' amount_due)
  const supplierUnpaidTotal = supplierFactures.reduce(
    (s, f) => s + (Number(f.amount_due) || 0),
    0
  );

  // Pick a supplier -> load its factures so we can apply an acompte
  const handlePickSupplier = async (s) => {
    setPickedSupplier(s);
    setSearchTerm(s.name);
    setShowSupplierSuggest(false);
    setAcompte('');
    try {
      const data = await factureService.getAllFactures({ supplier_id: s.id });
      setSupplierFactures(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching supplier factures:', err);
      setSupplierFactures([]);
    }
  };

  const clearPicked = () => {
    setSearchTerm('');
    setPickedSupplier(null);
    setSupplierFactures([]);
    setAcompte('');
    setShowSupplierSuggest(false);
  };

  const handleOpenAcompteModal = () => {
    setAcompte('');
    setAcompteError(null);
    setShowAcompteModal(true);
  };

  const handleApplyAcompte = async (e) => {
    e.preventDefault();
    const amount = round3(parseFloat(acompte));
    if (!amount || isNaN(amount) || amount <= 0) {
      setAcompteError('Veuillez entrer un montant valide.');
      return;
    }
    if (amount > supplierUnpaidTotal + 1e-6) {
      setAcompteError('Le montant dépasse le reste à payer du fournisseur.');
      return;
    }
    const unpaid = supplierFactures
      .filter((f) => (Number(f.amount_due) || 0) > 0)
      .sort(
        (a, b) =>
          new Date(a.date || a.date_added || 0) - new Date(b.date || b.date_added || 0)
      );

    setApplyingAcompte(true);
    setAcompteError(null);
    const EPS = 1e-6;
    // If the user is paying (essentially) the whole balance, clear it exactly so no
    // sub-millime float dust is left behind on the supplier.
    const payAll = amount >= supplierUnpaidTotal - 5e-4;
    let remaining = payAll ? supplierUnpaidTotal : amount;
    let totalPaid = 0;
    try {
      for (const f of unpaid) {
        if (remaining <= EPS) break;
        const due = Number(f.amount_due) || 0;
        if (due <= 0) continue;
        const pay = remaining >= due - EPS ? due : remaining;
        if (pay <= 0) continue;
        await factureService.updateFacturePayment(f.id, { amount: pay, skip_payment_log: true });
        remaining -= pay;
        totalPaid += pay;
      }
      // Record the acompte as one dated payment in the supplier's payment history.
      if (totalPaid > EPS) {
        try {
          await paymentService.create({
            payment_type: 'supplier',
            supplier: pickedSupplier.id,
            source: 'acompte',
            amount: round3(totalPaid),
            note: 'Acompte sur factures',
          });
        } catch (payErr) {
          // Don't fail the acompte if only the history record couldn't be written.
          console.error('Acompte recorded on factures but payment log failed:', payErr);
        }
      }
      setShowAcompteModal(false);
      setAcompte('');
      await fetchSuppliers();
      const data = await factureService.getAllFactures({ supplier_id: pickedSupplier.id });
      setSupplierFactures(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Acompte error:', err);
      setAcompteError("Erreur lors de l'enregistrement de l'acompte.");
    } finally {
      setApplyingAcompte(false);
    }
  };

  return (
    <div className="w-full px-4 py-6">
      {/* La bascule segmentée sert de titre (pleine largeur). */}
      <div className="mb-4">
        <FournisseursLabosSwitch full />
      </div>
      {/* Search bar (with supplier autocomplete for the acompte) */}
      <div className="relative mb-4">
        <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPickedSupplier(null);
            setSupplierFactures([]);
            setShowSupplierSuggest(true);
          }}
          onFocus={() => setShowSupplierSuggest(true)}
          onBlur={() => setTimeout(() => setShowSupplierSuggest(false), 150)}
          placeholder="Rechercher un fournisseur (nom, email ou téléphone)..."
          className="w-full pl-11 pr-10 py-2.5 border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
          autoComplete="off"
        />
        {(searchTerm || pickedSupplier) && (
          <button
            type="button"
            onClick={clearPicked}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Effacer"
          >
            <IcClose />
          </button>
        )}
        {showSupplierSuggest && !pickedSupplier && (supplierSuggestions.length > 0 || canManage) && (
          <div className="absolute left-0 right-0 top-full mt-2 z-20 bg-white rounded-xl shadow-lg border border-gray-100 py-1 max-h-72 overflow-auto">
            {supplierSuggestions.map((s) => (
              <button
                key={s.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePickSupplier(s)}
                className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-gray-50"
              >
                <span className="text-sm font-medium text-gray-800 min-w-0 truncate">{s.name}</span>
                {s.phone && <span className="text-xs text-gray-400 shrink-0 ml-2">{s.phone}</span>}
              </button>
            ))}
            {/* Ajouter un fournisseur — visible dès le tap sur la recherche,
                même motif que « Ajouter un nouveau produit » (AddFacturation).
                Remplace le FAB « + » (retiré, Ahmed 2026-08-17). */}
            {canManage && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => navigate('/suppliers/add')}
                className={`btn-add-link w-full text-left px-4 py-2.5 hover:bg-gray-100 ${supplierSuggestions.length > 0 ? 'border-t border-gray-100' : ''}`}
              >
                <IcPlus className="mr-1" />
                Ajouter un fournisseur
              </button>
            )}
          </div>
        )}
      </div>

      {/* Acompte card — same logic as the desktop supplier list */}
      {pickedSupplier && (
        /* Carte KPI blanche (Composants §5) — valeur rouge = reste dû. */
        <div className="kpi mb-4 rounded-2xl border-2 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="kpi-label uppercase text-sm tracking-normal truncate pr-2">
              Reste à payer · {pickedSupplier.name}
            </span>
            <span className="h-9 w-9 shrink-0 rounded-xl flex items-center justify-center bg-red-100 text-red-600">
              <IcAlertCircle />
            </span>
          </div>
          <p className="kpi-value text-2xl text-red-700">
            {supplierUnpaidTotal.toFixed(3)} DT
          </p>
          {supplierUnpaidTotal > 0.0005 ? (
            <button
              onClick={handleOpenAcompteModal}
              className="btn-p mt-3 w-full flex items-center justify-center gap-2 text-sm py-2 rounded-xl transition-colors"
            >
              <IcBanknote /> Enregistrer un acompte
            </button>
          ) : (
            <p className="text-xs text-green-700 mt-2 font-medium">
              Tout est payé pour ce fournisseur
            </p>
          )}
        </div>
      )}

      {error && (
        <div
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4"
          role="alert"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : filteredSuppliers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcBuilding className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">
            {searchTerm
              ? 'Aucun fournisseur correspondant à la recherche'
              : 'Aucun fournisseur trouvé'}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredSuppliers.map((supplier) => {
            const amountDue = Number(supplier.total_amount_due) || 0;
            const factureCount = supplier.facture_count || 0;
            const initials = (supplier.name || '?')
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((w) => w[0])
              .join('')
              .toUpperCase();
            return (
              <div
                key={supplier.id}
                onClick={() => navigate(`/suppliers/${supplier.id}`)}
                className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-200 cursor-pointer"
              >
                <div className="h-14 w-14 rounded-xl bg-gray-100 text-gray-500 flex-shrink-0 flex items-center justify-center font-semibold">
                  {initials || <IcBuilding />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">{supplier.name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {supplier.phone ? <PhoneLink phone={supplier.phone} /> : (supplier.email || 'Contact non renseigné')}
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    {/* Étiquettes : compteur neutre · reste dû = aplat critique
                        (montant TOUJOURS dans le badge) · Soldé = positif. */}
                    <span className="bdg bdg-neutral py-0.5 rounded-full">
                      <IcReceiptText />
                      {factureCount} facture{factureCount > 1 ? 's' : ''}
                    </span>
                    <span className={`bdg tabular-nums ${amountDue > 0 ? 'bdg-critical' : 'bdg-pos'} py-0.5 rounded-full`}>
                      {amountDue > 0 ? <IcAlertCircle /> : <IcBanknote />}
                      {amountDue > 0 ? `Reste ${formatDue(amountDue)}` : 'Soldé'}
                    </span>
                  </div>
                </div>

                <IcChevronRight className="text-gray-300 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}

      {/* FAB « + » retiré (Ahmed 2026-08-17) — l'ajout passe par la ligne
          « Ajouter un fournisseur » sous la barre de recherche. */}

      {/* Acompte Modal — same design & flow as the desktop supplier list */}
      {showAcompteModal && pickedSupplier && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowAcompteModal(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-lg font-semibold text-gray-800">Enregistrer un acompte</h2>
              <button
                onClick={() => setShowAcompteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <IcClose />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5">{pickedSupplier.name}</p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Reste à payer (total)</span>
                <span className="font-semibold text-red-700 tabular-nums">
                  {supplierUnpaidTotal.toFixed(3)} DT
                </span>
              </div>
              <p className="text-xs text-gray-400 pt-1">
                L'acompte est appliqué aux factures impayées, des plus anciennes aux plus
                récentes.
              </p>
            </div>

            <form onSubmit={handleApplyAcompte}>
              {acompteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mt-4">
                  {acompteError}
                </div>
              )}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  Montant de l'acompte
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={acompte}
                    onChange={(e) => setAcompte(e.target.value)}
                    min="0"
                    max={supplierUnpaidTotal}
                    step="0.001"
                    className="inp w-full rounded-xl px-4 py-3 text-base tabular-nums"
                    placeholder="0.000"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    DT
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={applyingAcompte}
                className="btn-p mt-6 w-full px-6 py-2.5 rounded-xl transition-colors"
              >
                {applyingAcompte ? 'Enregistrement...' : "Confirmer l'acompte"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
